import { useEffect, useMemo } from 'react';
import { Annotation } from '../../Annotation/types';
import { applyHighlights } from '../../Highlighting/utils/highlightTransformer';
import { styled } from '@mui/material/styles';
import { useSessionContext } from '../../../components/SessionContext';
import { transformContent } from '../engine';

interface RangeRendererOptions {
  renderHighlights?: boolean;
}

interface RangeRendererProps {
  htmlString: string;
  annotations?: Annotation[];
  renderOptions?: RangeRendererOptions;
}

const RangeContentContainer = styled('div')({
  '& img': {
    maxWidth: '100%',
    width: '100%',
  },
});

export function RangeRenderer({
  htmlString,
  annotations = [],
  renderOptions = { renderHighlights: false },
}: Readonly<RangeRendererProps>) {
  // Always sort highlights (even if we don't use them)
  const { currentChapter, loadedSection, trackProgress } = useSessionContext();

  const sortedAnnotations = useMemo(
    () =>
      [...annotations].sort(
        (a, b) =>
          a.position.start.blockId - b.position.start.blockId ||
          a.position.start.offset - b.position.start.offset,
      ),
    [annotations],
  );

  // Always transform HTML to React components, conditionally applying highlights
  const transformedContent: React.ReactNode[] = useMemo(() => {
    if (renderOptions.renderHighlights) {
      // Remove all instances of <d/> tags (with any amount of whitespace)
      const cleanHtmlString = htmlString.replace(/<d\s*\/?>/g, '');
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleanHtmlString, 'text/html');

      return Array.from(doc.body.childNodes).map((node) =>
        applyHighlights(node, sortedAnnotations),
      );
    } else {
      return transformContent(htmlString, sortedAnnotations);
    }
  }, [htmlString, sortedAnnotations, renderOptions.renderHighlights]);

  useEffect(() => {
    // For React elements, wait for next tick. This is to make sure that the transformedContent has finished transforming.
    setTimeout(() => {
      if (currentChapter?.nodeId !== loadedSection?.nodeId) {
        const chapterElement = document.getElementById(
          `b${currentChapter?.blockId}`,
        );
        if (chapterElement) {
          chapterElement.scrollIntoView({ behavior: 'auto' });
        }
      }
    }, 0);
  }, [transformedContent, currentChapter, loadedSection]);

  // Ensure progress tracker is running/observing when chapter changes
  useEffect(() => {
    if (loadedSection) {
      trackProgress();
    }
  }, [loadedSection, trackProgress]);

  // Always render as React components - no more dangerouslySetInnerHTML
  return <RangeContentContainer>{transformedContent}</RangeContentContainer>;
}
